import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { PaginationCommon } from '../PaginationCommon/PaginationCommon'
import { TableCommon } from '../TableCommon/TableCommon'
import { formatNumber } from '../../utils/function.utils'
import { DEFAULT_PAGE_SIZE } from '../../utils/constant'

function toNum(n, fallback) {
	const v = Number(n)
	return Number.isFinite(v) ? v : fallback
}

export const PagedResourceList = ({
	resource,
	columns,
	fetchPage,
	rowKey,
	header,
	headerClassName,
	topLeft,
	topRight,
	tableProps,
	fetchOnMount,
	pageSize: pageSizeProp,
	onPageSizeChange,
	showSizeChanger = true,
	pageSizeOptions,
	tableDataSource,
	paginationTotal,
}) => {
	const [internalPageSize, setInternalPageSize] = useState(DEFAULT_PAGE_SIZE)
	const isControlled =
		pageSizeProp !== undefined && typeof onPageSizeChange === 'function'
	const pageSize = isControlled ? pageSizeProp : internalPageSize
	const normalizedPageSize = toNum(pageSize, DEFAULT_PAGE_SIZE)

	const setPageSize = size => {
		const next = toNum(size, DEFAULT_PAGE_SIZE)
		if (isControlled) {
			onPageSizeChange(next)
		} else {
			setInternalPageSize(next)
		}
	}

	useEffect(() => {
		if (fetchOnMount && fetchPage) {
			const size = isControlled
				? toNum(pageSizeProp, DEFAULT_PAGE_SIZE)
				: internalPageSize
			fetchPage(0, size)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when fetchPage identity or fetchOnMount toggles
	}, [fetchOnMount, fetchPage])

	/**
	 * antd 4: page flips use onChange; page-size uses onShowSizeChange
	 * (PaginationCommon forwards both here). Prefer event pageSize when present.
	 *
	 * Controlled lists (e.g. OrderList) close over pageSize and treat the 2nd
	 * fetchPage arg as a callback — do not pass a numeric size to them.
	 */
	const handlePaginationChange = (page, size) => {
		const sizeFromEvent =
			size !== undefined ? toNum(size, normalizedPageSize) : undefined
		const nextSize =
			sizeFromEvent !== undefined ? sizeFromEvent : normalizedPageSize
		const sizeChanged = nextSize !== normalizedPageSize

		if (sizeChanged) {
			setPageSize(nextSize)
			if (!isControlled) {
				fetchPage(0, nextSize)
			}
			// Controlled: parent setState(pageSize) → useEffect / new fetchPage
			return
		}

		if (isControlled) {
			fetchPage(page - 1)
		} else {
			fetchPage(page - 1, normalizedPageSize)
		}
	}

	const totalValue =
		paginationTotal !== undefined && paginationTotal !== null
			? paginationTotal
			: resource && resource.total

	const tableRows =
		tableDataSource !== undefined && tableDataSource !== null
			? tableDataSource
			: resource && resource.data

	const defaultTopRight = (
		<>
			<span>Total:</span>&nbsp;
			<strong>{formatNumber(totalValue)}</strong>
		</>
	)

	const headerNode = header ? (
		header
	) : (
		<div className={headerClassName || 'u-top'}>
			<div>{topLeft}</div>
			<div>{topRight !== undefined ? topRight : defaultTopRight}</div>
		</div>
	)

	return (
		<>
			{headerNode}
			<TableCommon
				columns={columns}
				dataSource={tableRows}
				pagination={false}
				rowKey={rowKey}
				{...tableProps}
			/>
			<PaginationCommon
				align="center"
				total={resource && resource.total}
				currentPage={resource && resource.page}
				pageSize={normalizedPageSize}
				onChangePage={handlePaginationChange}
				showSizeChanger={showSizeChanger}
				pageSizeOptions={pageSizeOptions}
			/>
		</>
	)
}

PagedResourceList.propTypes = {
	resource: PropTypes.object,
	columns: PropTypes.array.isRequired,
	fetchPage: PropTypes.func.isRequired,
	rowKey: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
	header: PropTypes.node,
	headerClassName: PropTypes.string,
	topLeft: PropTypes.node,
	topRight: PropTypes.node,
	tableProps: PropTypes.object,
	fetchOnMount: PropTypes.bool,
	pageSize: PropTypes.number,
	onPageSizeChange: PropTypes.func,
	showSizeChanger: PropTypes.bool,
	pageSizeOptions: PropTypes.arrayOf(PropTypes.string),
	tableDataSource: PropTypes.array,
	paginationTotal: PropTypes.number,
}

PagedResourceList.defaultProps = {
	fetchOnMount: true,
}
